/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import type { FC } from 'react';
import React from 'react';
import { styled } from '@compiled/react';
import { useQuery } from '@apollo/react-hooks';
import { useIntl } from 'react-intl-next';

import { token } from '@atlaskit/tokens';
import type { SizeType } from '@atlaskit/avatar/types';
import { Text, Inline, Box, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import PageIcon from '@atlaskit/icon/core/page';
import { CommentEdited } from '@atlaskit/comment';

import { ProfileAvatar, ProfileLink } from '@confluence/profile';
import { TimeAgo } from '@confluence/time-ago';
import { ExternalCollaboratorUserLozenge } from '@confluence/external-collab-ui';
import { AnalyticsSource, CommentType } from '@confluence/comments-util/entry-points/analytics';
import { getValidDate } from '@confluence/comments-util/entry-points/dateUtils';
import type { FooterResolvedProperties } from '@confluence/comments-panel-queries';
import { SitePermissionTypeValues } from '@confluence/inline-comments-queries';
import type {
	SitePermissionType,
	InlineCommentAuthorQuery_content_nodes_history_createdBy_KnownUser,
	InlineCommentAuthorQuery_content_nodes_history_createdBy_User,
	CommentInlineResolveProperties,
} from '@confluence/inline-comments-queries';
import { fg } from '@confluence/feature-gating';

import type {
	CommentAuthorUserQuery as CommentAuthorUserQuery$data,
	CommentAuthorUserQueryVariables as CommentAuthorUserQuery$variables,
} from './__types__/CommentAuthorUserQuery';
import type {
	CommentAuthorInlineCommentAuthorQuery as CommentAuthorInlineCommentAuthorQuery$data,
	CommentAuthorInlineCommentAuthorQueryVariables as CommentAuthorInlineCommentAuthorQuery$variables,
} from './__types__/CommentAuthorInlineCommentAuthorQuery';
import type { CommentMode } from './inlineCommentsTypes';
import { CopyLinkButton } from './CopyLinkButton';
import { i18n } from './inlineCommentsi18n';

const CommentAuthorInlineCommentAuthorQuery = gql`
	query CommentAuthorInlineCommentAuthorQuery($commentId: ID!) {
		content(id: $commentId) {
			nodes {
				id
				history {
					createdBy {
						displayName
						profilePicture {
							path
						}
						... on KnownUser {
							accountId
						}
						... on User {
							accountId
						}
					}
				}
			}
		}
	}
`;
const CommentAuthorUserQuery = gql`
	query CommentAuthorUserQuery($id: ID!) {
		user: confluence_atlassianUser(id: $id) {
			id
			displayName
			photos {
				value
			}
		}
	}
`;
type CommentAuthorProps = {
	commentMode: CommentMode;
	userId?: string | null;
	avatarUrl?: string | null;
	displayName?: string | null;
	date?: string | null;
	createdAtNonLocalized?: string | null;
	versionByDisplayName?: string | null;
	versionDate?: string | null;
	size?: SizeType;
	commentDateUrl?: string;
	commentId?: string | null;
	permissionType?: SitePermissionType;
	isInactiveComment?: boolean;
	showCopyLink?: boolean;
	resolveProperties?: CommentInlineResolveProperties | FooterResolvedProperties;
	isCommentsPanel?: boolean;
	isGeneralComments?: boolean;
	isReply?: boolean;
	isEdited?: boolean;
	isFooterComment?: boolean;
	isStandaloneComment?: boolean;
};

type User =
	| InlineCommentAuthorQuery_content_nodes_history_createdBy_KnownUser
	| InlineCommentAuthorQuery_content_nodes_history_createdBy_User;

type AuthorContainerProps = {
	commentMode: string;
};

type DateContainerProps = {
	isStandaloneComment: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const AuthorContainer = styled.div<AuthorContainerProps>(`
  display: flex;
  align-items: center;
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
  margin-left: ${(props: AuthorContainerProps) => (props.commentMode === 'reply' ? '-2px' : 'inherit')};
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
  padding-bottom: ${(props: AuthorContainerProps) => (props.commentMode !== 'view' ? '8px;' : undefined)};

  > a {
    color: ${token('color.text')} !important;

    &:visited,
    &:hover {
      color: ${token('color.text')} !important;
      text-decoration: none !important;
    }
  }
  overflow: "hidden";
  `);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const AvatarWrapper = styled.div<{
	isCommentsPanel: boolean;
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
}>(`
	display: 'flex';
	${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
	padding-right: ${(props: { isCommentsPanel: boolean }) =>
		props.isCommentsPanel ? token('space.075') : token('space.100')};
	`);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	`
  display: flex;
  flex-direction: column;
  > a {
    color: ${token('color.text')} !important;
	font: ${token('font.body')};
    &:visited,
    &:hover {
      color: ${token('color.text')} !important;
    }
    &:focus-visible {
      outline: 2px solid ${token('color.border.focused')};
    }
  }
  > span.comment-timestamp {
    font: ${token('font.body.small')};
    color: ${token('color.text.subtle')};
    margin: 0;
    background-color: transparent;
    border: none;

    > a {
      color: ${token('color.text.subtle')};

      &:visited,
      &:hover {
        color: ${token('color.text.subtle')};
      }
    }
  }
`,
);

const resolvedPropertiesStyle = xcss({
	paddingLeft: 'space.050',
});

const pageIconStyle = xcss({
	position: 'relative',
	top: 'space.negative.025',
});

const generalCommentFooterStyle = xcss({
	top: 'space.negative.050',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ProfileLinkContainer = styled(Container)({
	flexDirection: 'row',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LozengeWrapper = styled.span({
	paddingLeft: `${token('space.100')}`,
	verticalAlign: 'top',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DateContainer = styled.div<DateContainerProps>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  ${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
    flex-direction: ${(props: DateContainerProps) =>
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			props.isStandaloneComment && fg('confluence-frontend-redesigned-standalone-comment')
				? 'column'
				: 'unset'};
  ${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
     align-items: ${(props: DateContainerProps) =>
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				props.isStandaloneComment && fg('confluence-frontend-redesigned-standalone-comment')
					? 'flex-start'
					: 'unset'};

  > span {
    font: ${token('font.body.small')};
    ${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
    color: ${(props: DateContainerProps) => (props.isStandaloneComment ? token('color.text.subtlest') : token('color.text.subtle'))};
    margin: 0;
    background-color: transparent;
    border: none;

    > a {
      color: ${token('color.text.subtle')};

      &:visited,
      &:hover {
        color: ${token('color.text.subtle')};
      }
    }
  }

  > div {
    button:focus {
      box-shadow: 0 0 0 2px ${token('color.border.focused')};
    }
  }
`,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EditedContainer = styled.div<DateContainerProps>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	`
  color: ${token('color.text.subtlest')};
  font: ${token('font.body.UNSAFE_small')};
  ${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
    padding-left: ${(props: DateContainerProps) =>
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			props.isStandaloneComment && fg('confluence-frontend-redesigned-standalone-comment')
				? token('space.0')
				: token('space.100')};
	${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ''}
	align-self: ${(props: DateContainerProps) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		props.isStandaloneComment && fg('confluence-frontend-redesigned-standalone-comment')
			? 'flex-start'
			: 'unset'};
`,
);

export const CommentAuthor: FC<CommentAuthorProps> = ({
	commentMode,
	userId,
	date,
	createdAtNonLocalized,
	versionByDisplayName,
	versionDate,
	size,
	displayName,
	avatarUrl,
	commentDateUrl,
	commentId,
	permissionType,
	isInactiveComment,
	showCopyLink = false,
	resolveProperties,
	isCommentsPanel = false,
	isGeneralComments = false,
	isReply = false,
	isEdited = false,
	isFooterComment = false,
	isStandaloneComment = false,
}) => {
	const { data: authorData } = useQuery<
		CommentAuthorInlineCommentAuthorQuery$data,
		CommentAuthorInlineCommentAuthorQuery$variables
	>(CommentAuthorInlineCommentAuthorQuery, {
		variables: { commentId: commentId! },
		fetchPolicy: 'cache-and-network',
		skip: Boolean(userId || !commentId),
	});
	const { data: userData } = useQuery<
		CommentAuthorUserQuery$data,
		CommentAuthorUserQuery$variables
	>(CommentAuthorUserQuery, {
		variables: { id: userId! },
		fetchPolicy: 'cache-and-network',
		// Skip the query if data is provided or the user is anonymous
		skip: Boolean((avatarUrl && displayName) || !userId || commentId),
	});
	const author = (authorData?.content?.nodes || [])[0]?.history?.createdBy;
	const userAvatar = (
		author ? author.profilePicture?.path : (userData?.user?.photos || [])[0]?.value
	) as string | undefined;
	const userDisplayName = author ? author.displayName : userData?.user?.displayName;
	const userAccountId = (author as User)?.accountId;

	const linkToComment = `${window.location.origin}/wiki${commentDateUrl}`;
	const { formatMessage } = useIntl();

	const tooltipText = resolveProperties?.resolved
		? formatMessage(i18n.resolvedByTooltipTextCommentsPanel, {
				username: resolveProperties?.resolvedUser,
			})
		: formatMessage(i18n.reopenedByTooltipTextCommentsPanel, {
				username: resolveProperties?.resolvedUser,
				createdAtNonLocalized: (
					<TimeAgo date={getValidDate(createdAtNonLocalized)} usePrepositions />
				),
			});

	const oldStandloneExperienceResolveInfo = () => {
		return (
			<Tooltip content={resolveProperties?.resolvedFriendlyDate}>
				<Text color="color.text.subtlest" size="small" maxLines={2}>
					{formatMessage(i18n.resolvedByInfoText, {
						username: resolveProperties?.resolvedUser,
						friendlyDate: resolveProperties?.resolvedFriendlyDate,
					})}
				</Text>
			</Tooltip>
		);
	};

	const newExperienceResolveInfo = () => {
		return (
			<Tooltip position="top" content={tooltipText}>
				<Text color="color.text.subtlest" size="small" maxLines={2}>
					{resolveProperties?.resolved
						? formatMessage(i18n.resolvedDateInfoText, {
								friendlyDate: resolveProperties?.resolvedFriendlyDate,
							})
						: formatMessage(i18n.reopenedDateInfoText, {
								friendlyDate: resolveProperties?.resolvedFriendlyDate,
							})}
				</Text>
			</Tooltip>
		);
	};

	const renderResolveInfo = () => {
		const standAloneExperience = !isFooterComment && !isCommentsPanel;
		if (
			(standAloneExperience && fg('confluence-frontend-redesigned-standalone-comment')) ||
			(isFooterComment && fg('confluence-frontend-redesigned-footer-comment')) ||
			isCommentsPanel
		) {
			return newExperienceResolveInfo();
		}
		return oldStandloneExperienceResolveInfo();
	};

	const renderCopyButton = () => {
		const resolvedInfo = renderResolveInfo();

		const renderCopyLinkButton =
			commentDateUrl &&
			!fg('confluence-frontend-redesigned-standalone-comment') &&
			((isCommentsPanel && !fg('confluence_frontend_comments_panel_v2')) || !isCommentsPanel);

		const versionDisplayName = versionByDisplayName || formatMessage(i18n.anonymous);

		return (
			<DateContainer isStandaloneComment={isStandaloneComment}>
				{resolveProperties?.resolved || resolveProperties?.resolvedFriendlyDate ? (
					<>
						{resolvedInfo}
						{isGeneralComments &&
							!isReply &&
							fg('confluence-frontend-comments-panel-design-update') && (
								<Box
									xcss={[
										pageIconStyle,
										resolvedPropertiesStyle,
										isFooterComment && generalCommentFooterStyle,
									]}
								>
									<Tooltip content={formatMessage(i18n.generalCommentTooltip)}>
										<PageIcon
											color={token('color.icon.subtlest')}
											testId="general-comment-page-icon"
											label={formatMessage(i18n.generalCommentTooltip)}
										/>
									</Tooltip>
								</Box>
							)}
					</>
				) : (
					<span>
						<Inline space="space.050">
							<TimeAgo
								date={getValidDate(
									fg('confluence_frontend_cp_sort_new_to_old') ? createdAtNonLocalized : date,
								)}
							/>
							{isGeneralComments &&
								!isReply &&
								fg('confluence-frontend-comments-panel-design-update') && (
									<Box xcss={pageIconStyle}>
										<Tooltip content={formatMessage(i18n.generalCommentTooltip)}>
											<PageIcon
												color={token('color.icon.subtlest')}
												testId="general-comment-page-icon"
												label={formatMessage(i18n.generalCommentTooltip)}
											/>
										</Tooltip>
									</Box>
								)}
						</Inline>
					</span>
				)}
				{/* eslint-disable confluence-feature-gating/no-preconditioning */}
				{isEdited &&
					fg('confluence_frontend_cp_sort_new_to_old') &&
					!fg('confluence_frontend_comment_edited_label_v2') && (
						<EditedContainer isStandaloneComment={isStandaloneComment}>
							<Tooltip
								content={formatMessage(i18n.editedBy, {
									username: versionDisplayName,
									at: <TimeAgo date={getValidDate(versionDate)} usePrepositions />,
								})}
								analyticsContext={{
									attributes: {
										source: isCommentsPanel
											? AnalyticsSource.COMMENTS_PANEL
											: AnalyticsSource.STANDALONE,
										commentType: isGeneralComments ? CommentType.GENERAL : CommentType.INLINE,
										name: 'commentEditedBadgeTooltip',
									},
								}}
							>
								<span data-testid="comment-edited-label">
									<CommentEdited>{formatMessage(i18n.edited)}</CommentEdited>
								</span>
							</Tooltip>
						</EditedContainer>
					)}
				{renderCopyLinkButton && (
					<CopyLinkButton visible={showCopyLink} textToCopy={linkToComment} />
				)}
			</DateContainer>
		);
	};

	const isDateShown = commentMode === 'view' && date;
	const name = displayName || userDisplayName || '';
	return (
		<AuthorContainer commentMode={commentMode} data-cy="comment-author">
			<AvatarWrapper isCommentsPanel={isCommentsPanel}>
				<ProfileAvatar
					userId={userId}
					src={avatarUrl || userAvatar}
					trigger="click"
					size={size}
					name={name}
				/>
			</AvatarWrapper>
			<Container>
				<ProfileLinkContainer>
					{/* Show Dummy Link in Inactive Mode for View-All*/}
					{isInactiveComment ? (
						<span data-testid="dummy-profile-link">{displayName || userDisplayName}</span>
					) : (
						<ProfileLink
							fullName={(displayName || userDisplayName)!}
							userId={userId || userAccountId}
							style={{
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
								wordBreak: 'break-word',
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
								color: token('color.text'),
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
								fontWeight: token('font.weight.medium'),
							}}
							isUnderlinedByDefault={false}
						/>
					)}
					{permissionType === SitePermissionTypeValues.EXTERNAL ? (
						<LozengeWrapper>
							<ExternalCollaboratorUserLozenge />
						</LozengeWrapper>
					) : null}
				</ProfileLinkContainer>
				{isDateShown && renderCopyButton()}
			</Container>
		</AuthorContainer>
	);
};
